/* eslint-disable */
import { Observable, Observer } from 'rxjs';
import { environment } from '../../environments/environment';
import { Order, OrderType, } from './util/services';
import { MemStorage } from './util/mem-storage';
import { Injectable } from '@angular/core';
import { MultiselectOptionItem, OptionItem, UiConstants } from '../util/core-utils';
import { Stock } from './stock/stock.service';
import { DisabledItem } from '../util/search-utils';
import { StockTypeFilterOption } from '../util/stock/stock-utils';
import { RuntimeConfiguration } from './runtime-configuration';
import { LoggedInUserStorage } from './util/storages';
/* eslint-enable */

@Injectable()
export class StockSearchService {

  constructor() {
  }

  getSearchData(request: StockSearch.SearchDataGetRequest): Observable<StockSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<StockSearch.SearchDataResult>) => {
      try {
        observer.next(storage.read(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  setSearchData(request: StockSearch.SearchDataSetRequest): Observable<StockSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<StockSearch.SearchDataResult>) => {
      try {
        observer.next(storage.save(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  resetSearchData(request: StockSearch.SearchDataGetRequest): Observable<StockSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<StockSearch.SearchDataResult>) => {
      try {
        observer.next(storage.reset(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  private createStorage(): StockSearch.Storage {
    return new StockSearch.Storage();
  }

}

export namespace StockSearch {

  export interface SearchDataGetRequest {
  }

  export interface SearchDataSetRequest {
    searchData: SearchData;
  }

  export interface SearchDataResult {
    searchData: SearchData;
  }

  export interface SearchData {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<Stock.OrderField>;
    id: string;
    name: string;
    ownerUsers: MultiselectOptionItem<number>[];
    companies: MultiselectOptionItem<number>[];
    customerRecord: MultiselectOptionItem<number>[];
    contactLocation: MultiselectOptionItem<number>[];
    stockItems: MultiselectOptionItem<number>[];
    type: StockTypeFilterOption;
    disabled?: DisabledItem;
  }

  interface StoredSearchRawDataBase {
    version: number;
  }

  interface StoredSearchRawData extends StoredSearchRawDataBase {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<Stock.OrderField>;
    id: string;
    name: string;
    ownerUsers: MultiselectOptionItem<number>[];
    companies: MultiselectOptionItem<number>[];
    customerRecord: MultiselectOptionItem<number>[];
    contactLocation: MultiselectOptionItem<number>[];
    stockItems: MultiselectOptionItem<number>[];
    type: StockTypeFilterOption;
    disabled?: DisabledItem;
  }

  export class Storage {

    /**
     * Increment this version if you change the SearchData interface.
     */
    private static readonly VERSION: number = 11;

    private static readonly DEFAULT_SEARCH_DATA: SearchData = {
      itemsPerPage: UiConstants.pageNumbers[0],
      pageNumber: 1,
      order: {
        field: Stock.OrderField.ID,
        type: OrderType.DESC
      },
      id: '',
      name: '',
      ownerUsers: [],
      companies: [],
      customerRecord: [],
      contactLocation: [],
      stockItems: [],
      type: 'ALL',
      disabled: DisabledItem.active()
    };

    constructor() {
    }

    public read(request: SearchDataGetRequest): SearchDataResult {
      let rawBase: StoredSearchRawDataBase;
      try {
        const key = this.generateKey();
        const json = MemStorage.getItem(key);
        if (!json) {
          return this.createDefaultResult();
        }
        rawBase = JSON.parse(json);
      }
      catch (error) {
        return this.createDefaultResult();
      }
      if (Storage.VERSION === rawBase.version) {
        const raw: StoredSearchRawData = <StoredSearchRawData> rawBase;
        return this.fromRaw(raw);
      }
      return this.createDefaultResult();
    }

    public reset(request: SearchDataGetRequest): SearchDataResult {
      return this.save({
        searchData: Storage.DEFAULT_SEARCH_DATA,
      });
    }

    public save(request: SearchDataSetRequest): SearchDataResult {
      const key = this.generateKey();
      const raw = this.toRaw(request.searchData);
      const json = JSON.stringify(raw);
      MemStorage.setItem(key, json);
      return this.read(request);
    }

    private generateKey(): string {
      return 'UserId(' + LoggedInUserStorage.getInstance().getUserId() + ')-Search-Stock';
    }

    private createDefaultResult(): SearchDataResult {
      return {
        searchData: Storage.DEFAULT_SEARCH_DATA,
      };
    }

    private fromRaw(data: StoredSearchRawData): SearchDataResult {
      return {
        searchData: {
          itemsPerPage: data.itemsPerPage,
          pageNumber: data.pageNumber,
          order: data.order,
          id: data.id,
          name: data.name,
          ownerUsers: data.ownerUsers,
          companies: data.companies,
          customerRecord: data.customerRecord,
          contactLocation: data.contactLocation,
          stockItems: data.stockItems,
          type: data.type,
          disabled: data.disabled
        }
      };
    }

    private toRaw(data: SearchData): StoredSearchRawData {
      return {
        version: Storage.VERSION,
        itemsPerPage: data.itemsPerPage,
        pageNumber: data.pageNumber,
        order: data.order,
        id: data.id,
        name: data.name,
        ownerUsers: data.ownerUsers,
        companies: data.companies,
        customerRecord: data.customerRecord,
        contactLocation: data.contactLocation,
        stockItems: data.stockItems,
        type: data.type,
        disabled: data.disabled
      };
    }

  }

}
